// Add Maintenance Panel Component
// Professional side panel for recording maintenance activities

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CalendarIcon, Loader2, Wrench, Coins, FileText, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Asset } from '@/types/asset';
import type { CreateMaintenanceDto } from '@/types/maintenance';
import { maintenanceService } from '@/services/maintenance-service';

interface AddMaintenancePanelProps {
  asset: Asset;
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  onSuccess?: () => void;
}

const MAINTENANCE_TYPES = [
  'Service',
  'Repair',
  'Inspection',
  'Calibration',
  'Cleaning',
  'Upgrade',
  'Replacement',
  'Other',
] as const;

export function AddMaintenancePanel({
  asset,
  open,
  onOpenChange,
  onClose,
  onSuccess,
}: Readonly<AddMaintenancePanelProps>) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [calculatedTax, setCalculatedTax] = useState(0);
  const [calculatedGross, setCalculatedGross] = useState(0);

  const [formData, setFormData] = useState<CreateMaintenanceDto>({
    assetId: asset.id,
    reference: '',
    maintenanceDate: new Date().toISOString().split('T')[0],
    maintenanceType: 'Service',
    description: '',
    supplierName: '',
    supplierReference: '',
    net: 0,
    taxRate: 20,
    workPerformed: '',
    partsReplaced: '',
    nextMaintenanceDate: undefined,
    warrantyWork: false,
    notes: '',
  });

  // Auto-calculate tax and gross when net cost or tax rate changes
  useEffect(() => {
    if (formData.warrantyWork) {
      // If warranty work, set all costs to 0
      setFormData(prev => ({ ...prev, net: 0, taxRate: 0 }));
      setCalculatedTax(0);
      setCalculatedGross(0);
      return;
    }

    const net = formData.net || 0;
    const taxRate = formData.taxRate || 0;
    const tax = net * (taxRate / 100);
    const gross = net + tax;
    setCalculatedTax(parseFloat(tax.toFixed(2)));
    setCalculatedGross(parseFloat(gross.toFixed(2)));
  }, [formData.net, formData.taxRate, formData.warrantyWork]);

  // Handle gross total change - back calculate net
  const handleGrossChange = (value: string) => {
    const gross = parseFloat(value) || 0;
    const taxRate = formData.taxRate || 0;
    const net = gross / (1 + taxRate / 100);
    const tax = gross - net;
    
    setFormData(prev => ({ ...prev, net: parseFloat(net.toFixed(2)) }));
    setCalculatedTax(parseFloat(tax.toFixed(2)));
    setCalculatedGross(parseFloat(gross.toFixed(2)));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.reference?.trim()) {
      newErrors.reference = 'Reference is required';
    }
    if (!formData.maintenanceDate) {
      newErrors.maintenanceDate = 'Maintenance date is required';
    }
    if (!formData.maintenanceType) {
      newErrors.maintenanceType = 'Maintenance type is required';
    }
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // Mark all fields as touched
    setTouched({
      reference: true,
      maintenanceDate: true,
      maintenanceType: true,
      description: true,
    });

    if (!validateForm()) {
      // Don't show toast - validation errors are shown inline
      return;
    }

    setIsSubmitting(true);
    try {
      await maintenanceService.create(formData);
      toast.success('Maintenance record created successfully');
      
      // Reset form
      setFormData({
        assetId: asset.id,
        reference: '',
        maintenanceDate: new Date().toISOString().split('T')[0],
        maintenanceType: 'Service',
        description: '',
        supplierName: '',
        supplierReference: '',
        net: 0,
        taxRate: 20,
        workPerformed: '',
        partsReplaced: '',
        nextMaintenanceDate: undefined,
        warrantyWork: false,
        notes: '',
      });
      setErrors({});
      setTouched({});
      setCalculatedTax(0);
      setCalculatedGross(0);
      
      onSuccess?.();
      handleClose();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create maintenance record';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (onOpenChange) {
      onOpenChange(false);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleFieldChange = (field: keyof CreateMaintenanceDto, value: string | number | boolean | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleWarrantyChange = (checked: boolean) => {
    handleFieldChange('warrantyWork', checked);
    if (checked) {
      setFormData(prev => ({ ...prev, net: 0, taxRate: 0 }));
    } else {
      setFormData(prev => ({ ...prev, taxRate: 20 }));
    }
  };

  // Format number to 2 decimal places
  const formatCurrency = (value: number) => {
    return value.toFixed(2);
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="flex w-[600px] max-w-[90vw] flex-col p-0 sm:max-w-[600px]">
        {/* Header */}
        <SheetHeader className="border-b bg-white px-6 pt-6 pb-4 dark:bg-gray-950">
          <SheetTitle className="text-xl font-semibold">Add Maintenance Record</SheetTitle>
        </SheetHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold">Basic Information</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Reference */}
                <div className="space-y-2">
                  <Label htmlFor="reference">
                    Reference <span className="text-red-500">*</span>
                  </Label>
                    <Input
                      id="reference"
                      value={formData.reference}
                      onChange={(e) => handleFieldChange('reference', e.target.value)}
                      placeholder="MAINT-2024-0001"
                      className={cn(
                        touched.reference && errors.reference && 'border-red-500'
                      )}
                    />
                    {touched.reference && errors.reference && (
                      <p className="text-sm text-red-500">{errors.reference}</p>
                    )}
                </div>

                {/* Maintenance Date */}
                <div className="space-y-2">
                  <Label htmlFor="maintenanceDate">
                    Maintenance Date <span className="text-red-500">*</span>
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal bg-white dark:bg-black',
                          !formData.maintenanceDate && 'text-muted-foreground',
                          touched.maintenanceDate && errors.maintenanceDate && 'border-red-500'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.maintenanceDate
                          ? format(new Date(formData.maintenanceDate), 'dd/MM/yyyy')
                          : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.maintenanceDate ? new Date(formData.maintenanceDate) : undefined}
                        onSelect={(date) =>
                          handleFieldChange('maintenanceDate', date?.toISOString().split('T')[0])
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {touched.maintenanceDate && errors.maintenanceDate && (
                    <p className="text-sm text-red-500">{errors.maintenanceDate}</p>
                  )}
                </div>

                {/* Maintenance Type */}
                <div className="space-y-2">
                  <Label htmlFor="maintenanceType">
                    Maintenance Type <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.maintenanceType}
                    onValueChange={(value: string) => handleFieldChange('maintenanceType', value)}
                  >
                    <SelectTrigger
                      className={cn(
                        touched.maintenanceType && errors.maintenanceType && 'border-red-500'
                      )}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MAINTENANCE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {touched.maintenanceType && errors.maintenanceType && (
                    <p className="text-sm text-red-500">{errors.maintenanceType}</p>
                  )}
                </div>

                {/* Description */}
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="description">
                    Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFieldChange('description', e.target.value)}
                    placeholder="Brief description of maintenance work"
                    rows={3}
                    className={cn(
                      touched.description && errors.description && 'border-red-500'
                    )}
                  />
                  {touched.description && errors.description && (
                    <p className="text-sm text-red-500">{errors.description}</p>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* Supplier Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold">Supplier Information</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Supplier Name */}
                <div className="space-y-2">
                  <Label htmlFor="supplierName">Supplier Name</Label>
                  <Input
                    id="supplierName"
                    value={formData.supplierName}
                    onChange={(e) => handleFieldChange('supplierName', e.target.value)}
                    placeholder="Enter supplier name"
                  />
                </div>

                {/* Supplier Reference */}
                <div className="space-y-2">
                  <Label htmlFor="supplierReference">Supplier Invoice/Reference</Label>
                  <Input
                    id="supplierReference"
                    value={formData.supplierReference}
                    onChange={(e) => handleFieldChange('supplierReference', e.target.value)}
                    placeholder="INV-12345"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Cost Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Coins className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold">Cost Information</h3>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Warranty Work Checkbox */}
                <div className="col-span-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="warrantyWork"
                      checked={formData.warrantyWork}
                      onCheckedChange={handleWarrantyChange}
                    />
                    <Label
                      htmlFor="warrantyWork"
                      className="cursor-pointer font-normal"
                    >
                      Costs covered by existing warranty cover
                    </Label>
                  </div>
                </div>
                {/* Net Cost */}
                <div className="space-y-2">
                  <Label htmlFor="net">Net (Cost excl. tax)</Label>
                  <Input
                    id="net"
                    type="number"
                    step="0.01"
                    value={formData.net}
                    onChange={(e) => handleFieldChange('net', parseFloat(e.target.value) || 0)}
                    onBlur={(e) => {
                      if (e.target.value) {
                        const formatted = parseFloat(e.target.value).toFixed(2);
                        handleFieldChange('net', parseFloat(formatted));
                      }
                    }}
                    placeholder="0.00"
                    disabled={formData.warrantyWork}
                  />
                </div>

                {/* Tax Rate */}
                <div className="space-y-2">
                  <Label htmlFor="taxRate">Tax Rate (%)</Label>
                  <Input
                    id="taxRate"
                    type="number"
                    step="0.01"
                    value={formData.taxRate}
                    onChange={(e) => handleFieldChange('taxRate', parseFloat(e.target.value) || 0)}
                    onBlur={(e) => {
                      if (e.target.value) {
                        const formatted = parseFloat(e.target.value).toFixed(2);
                        handleFieldChange('taxRate', parseFloat(formatted));
                      }
                    }}
                    placeholder="20.00"
                    disabled={formData.warrantyWork}
                  />
                </div>

                {/* Tax Amount (Calculated) */}
                <div className="space-y-2">
                  <Label htmlFor="taxAmount">Tax Amount</Label>
                  <Input
                    id="taxAmount"
                    value={formatCurrency(calculatedTax)}
                    readOnly
                    className="bg-muted/50 text-muted-foreground cursor-default border-border/50"
                  />
                </div>

                {/* Gross Total */}
                <div className="space-y-2">
                  <Label htmlFor="grossTotal">Gross Total (Cost incl. tax)</Label>
                  <Input
                    id="grossTotal"
                    type="number"
                    step="0.01"
                    value={calculatedGross}
                    onChange={(e) => handleGrossChange(e.target.value)}
                    onBlur={(e) => {
                      if (e.target.value) {
                        const formatted = parseFloat(e.target.value).toFixed(2);
                        setCalculatedGross(parseFloat(formatted));
                      }
                    }}
                    placeholder="0.00"
                    disabled={formData.warrantyWork}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Work Details */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold">Work Details</h3>
              </div>
              <div className="space-y-4">
                {/* Work Performed */}
                <div className="space-y-2">
                  <Label htmlFor="workPerformed">Work Performed</Label>
                  <Textarea
                    id="workPerformed"
                    value={formData.workPerformed}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFieldChange('workPerformed', e.target.value)}
                    placeholder="Describe the work that was performed"
                    rows={3}
                  />
                </div>

                {/* Parts Replaced */}
                <div className="space-y-2">
                  <Label htmlFor="partsReplaced">Parts Replaced</Label>
                  <Textarea
                    id="partsReplaced"
                    value={formData.partsReplaced}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFieldChange('partsReplaced', e.target.value)}
                    placeholder="List any parts that were replaced"
                    rows={3}
                  />
                </div>

                {/* Additional Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFieldChange('notes', e.target.value)}
                    placeholder="Any additional notes or observations"
                    rows={6}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <SheetFooter className="border-t bg-white px-6 py-4 dark:bg-gray-950">
          <div className="flex w-full gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create'
              )}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
